import React, {useMemo} from "react"
import styles from "./DailyAttendanceCalendarView.module.css"
import cx from "classnames"
import {roundTwoDigits} from "helpers"
import {AttendancePriorTotals} from "sections/shared/attendanceCalendarUtils"

type SummaryRow = {
    label: string
    total: number
    priorTotal?: number | null
}

type SummaryViewProps = {
    dailyAttendanceSummaryData: any
    title?: string
    includeLoaDays?: boolean
    includeScheduleHoursToLda?: boolean
    className?: string
    showPriorTotal?: boolean
    priorTotals?: AttendancePriorTotals | null
    totalColumnLabel?: string
}

const SummaryView = ({
    dailyAttendanceSummaryData,
    title = "Summary",
    includeLoaDays = true,
    includeScheduleHoursToLda = true,
    className,
    showPriorTotal = false,
    priorTotals,
    totalColumnLabel = "Total"
}: SummaryViewProps) => {
    const tableData = useMemo((): SummaryRow[] => {
        const {
            totalAttendedHours,
            totalMissedHours,
            totalMakeUpHours,
            totalDistEdHours,
            totalScheduleHours,
            totalScheduleHoursToLda,
            totalLoaDays
        } = dailyAttendanceSummaryData ?? {}

        const rows: SummaryRow[] = [
            {
                label: "Scheduled Hours",
                total: totalScheduleHours ?? 0,
                priorTotal: priorTotals?.totalScheduleHours
            }
        ]

        if (includeScheduleHoursToLda) {
            rows.push({
                label: "Scheduled Hours To LDA",
                total: totalScheduleHoursToLda ?? 0,
                priorTotal: priorTotals?.totalScheduleHoursToLda
            })
        }

        rows.push(
            {
                label: "Attended Hours",
                total: totalAttendedHours ?? 0,
                priorTotal: priorTotals?.totalAttendedHours
            },
            {
                label: "Absent Hours",
                total: totalMissedHours ?? 0,
                priorTotal: priorTotals?.totalMissedHours
            },
            {
                label: "Makeup Hours",
                total: totalMakeUpHours ?? 0,
                priorTotal: priorTotals?.totalMakeUpHours
            },
            {
                label: "Distance Ed Hours",
                total: totalDistEdHours ?? 0,
                priorTotal: priorTotals?.totalDistEdHours
            }
        )

        if (includeLoaDays) {
            rows.push({label: "Loa Days", total: totalLoaDays ?? 0})
        }

        return rows
    }, [dailyAttendanceSummaryData, includeLoaDays, includeScheduleHoursToLda, priorTotals])

    return (
        <div className={cx(styles.summaryContainer, className)}>
            <div className={cx(styles.summaryWrap, showPriorTotal && styles.summaryWrapWithPrior)}>
                <div className={styles.summaryTitle}>{title}</div>
                <table className={styles.table}>
                    <thead>
                        <tr className={styles.headerRow}>
                            <th className={styles.th}>Type</th>
                            {showPriorTotal ? <th className={cx(styles.th, styles.thRight)}>Prior Total</th> : null}
                            <th className={styles.th}>{totalColumnLabel}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.map((row) => (
                            <tr key={row.label}>
                                <td className={cx(styles.td, styles.rowLabel)}>{row.label}</td>
                                {showPriorTotal ? (
                                    <td className={styles.td}>
                                        {row.priorTotal == null ? "" : roundTwoDigits(row.priorTotal)}
                                    </td>
                                ) : null}
                                <td className={styles.td}>{roundTwoDigits(row.total)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default SummaryView
